import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Check, Tag, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { api } from "../api";
import PlanCard from "./PlanCard";
import { useToast } from "../hooks/use-toast";

const ICONS = { menus: "🍽️", pages: "🔗", qr: "▦" };

export default function Services() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [code, setCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [couponMsg, setCouponMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.myServices(); setData(data); };
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-muted-foreground">Caricamento…</p>;

  const { services, catalog, bundle_price } = data;
  const toggle = (k) => { setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]); setQuote(null); setCouponMsg(null); };

  const applyCoupon = async () => {
    if (!code || sel.length === 0) { toast({ title: "Seleziona i servizi e inserisci un codice" }); return; }
    try {
      const { data } = await api.validateCoupon({ code, services: sel });
      if (!data.valid) { setCouponMsg({ ok: false, text: data.reason }); setQuote(null); }
      else { setCouponMsg({ ok: true, text: "Coupon applicato" }); setQuote(data.quote); }
    } catch (e) { setCouponMsg({ ok: false, text: e?.response?.data?.detail || "Errore" }); }
  };

  const activate = async () => {
    if (sel.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.activateServices({ services: sel, coupon_code: code || null });
      toast({ title: data.status === "activated" ? "Servizi attivati" : "Attivazione in attesa", description: data.message });
      setSel([]); setCode(""); setQuote(null); setCouponMsg(null); load();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const subtotal = quote ? quote.subtotal : sel.reduce((a, k) => a + catalog[k].price, 0);
  const total = quote ? quote.total : (sel.length === 3 ? bundle_price : subtotal);

  return (
    <div>
      <div className="mb-6"><h1 className="font-display text-2xl md:text-3xl font-extrabold">Servizi</h1><p className="text-muted-foreground mt-1">Attiva solo ciò che ti serve. Puoi aggiungere altri servizi in qualsiasi momento.</p></div>
      <PlanCard />

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {Object.entries(catalog).map(([k, v]) => {
          const active = services[k]?.active;
          const picked = sel.includes(k);
          return (
            <div key={k} className={`rounded-2xl border bg-white p-5 card-shadow ${picked ? "border-primary ring-2 ring-primary/20" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <span className="text-2xl">{ICONS[k]}</span>
                {active ? <Badge className="bg-emerald-50 text-emerald-600 gap-1 text-[10px]"><ShieldCheck className="h-3 w-3" /> Attivo</Badge> : <Badge variant="outline" className="text-[10px] text-muted-foreground">Non attivo</Badge>}
              </div>
              <h3 className="font-display font-bold mt-3">{v.name}</h3>
              <p className="font-display text-2xl font-extrabold mt-1">€{v.price}<span className="text-sm text-muted-foreground font-normal">/mese</span></p>
              <p className="text-xs text-muted-foreground mt-1">{active ? `Fonte: ${services[k].source}` : "Attivalo per iniziare"}</p>
              {!active && (
                <Button variant={picked ? "default" : "outline"} className={`w-full mt-4 font-semibold ${picked ? "btn-grad text-white" : ""}`} onClick={() => toggle(k)}>
                  {picked ? <><Check className="mr-1.5 h-4 w-4" /> Selezionato</> : "Seleziona"}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {sel.length > 0 && (
        <div className="rounded-2xl border border-border bg-white p-5 card-shadow max-w-lg">
          <h3 className="font-semibold mb-3">Riepilogo attivazione</h3>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Codice sconto" className="pl-9 h-10" />
            </div>
            <Button variant="outline" onClick={applyCoupon}>Applica</Button>
          </div>
          {couponMsg && <p className={`text-xs mb-3 ${couponMsg.ok ? "text-emerald-600" : "text-destructive"}`}>{couponMsg.text}</p>}
          <div className="space-y-1.5 text-sm">
            {(quote ? quote.lines : sel.map((k) => ({ service: k, name: catalog[k].name, price: catalog[k].price, discount: 0, net: catalog[k].price }))).map((l) => (
              <div key={l.service} className="flex justify-between">
                <span>{l.name}</span>
                <span>{l.discount > 0 ? <><span className="line-through text-muted-foreground mr-1">€{l.price}</span>€{l.net}</> : `€${l.price}`}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold pt-2 border-t border-border mt-2"><span>Totale</span><span>€{total}{total > 0 ? "/mese" : ""}</span></div>
            {quote?.benefit_duration && <p className="text-xs text-muted-foreground">Durata beneficio: {quote.benefit_duration === "full" ? "intera durata" : quote.benefit_duration === "months" ? `${quote.benefit_months} mesi` : "primo periodo"}{quote.applies_to_renewals ? " (anche sui rinnovi)" : ""}</p>}
          </div>
          {total === 0 ? (
            <Button className="w-full btn-grad text-white font-semibold mt-4" onClick={activate} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Attiva gratis (nessuna carta richiesta)"}</Button>
          ) : (
            <>
              <Button className="w-full btn-grad text-white font-semibold mt-4" onClick={() => navigate("/prezzi")} data-testid="services-go-pricing">Prova gratis 7 giorni o attiva un piano</Button>
              <p className="text-[11px] text-muted-foreground mt-2">I moduli premium si attivano con la prova gratuita (carta richiesta, 0 € oggi) o con un abbonamento Qora tramite Stripe. Hai un codice? Applicalo qui sopra.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
